<template>
  <div class="page-shell">
    <div v-if="isLoadingDepartments" class="align-middle d-flex justify-content-center py-5">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <template v-else>
      <CRow v-if="!isModerator">
        <CCol xs="12" sm="8" md="6" lg="4">
          <CCard>
            <CCardHeader>
              <CCardTitle class="mb-0">Add Priority Number</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol xs="12">
                  <CAlert
                    v-if="formMessage"
                    :color="formMessageColor"
                    :show="alertDuration"
                    closeButton
                    @update:show="alertDuration = $event"
                  >
                    {{ formMessage }}
                    <CProgress
                      :color="formMessageColor"
                      :value="10 * Number(alertDuration)"
                      size="xs"
                      class="mb-3"
                    />
                  </CAlert>
                </CCol>
              </CRow>

              <CRow>
                <CCol>
                  <CFormGroup>
                    <CLabel for="priority-phone">Customer Phone</CLabel>
                    <CInput
                      id="priority-phone"
                      :value="form.customerPhone"
                      type="number"
                      placeholder="Enter Customer Phone"
                      @input="form.customerPhone = $event.target ? $event.target.value : $event"
                    />
                  </CFormGroup>
                </CCol>
              </CRow>

              <CRow>
                <CCol>
                  <CFormGroup>
                    <CLabel>Customer Name</CLabel>
                    <CInput
                      :value="form.customerName"
                      type="text"
                      placeholder="Enter Customer Name"
                      @input="form.customerName = $event.target ? $event.target.value : $event"
                    />
                  </CFormGroup>
                </CCol>
              </CRow>

              <CRow>
                <CCol>
                  <CFormGroup>
                    <CLabel>Assign Deaprtment</CLabel>
                    <CSelect
                      :value="form.departmentId"
                      @change="form.departmentId = $event.target ? $event.target.value : $event"
                    >
                      <option :value="0">Any Department</option>
                      <option v-for="department in departments" :key="department.id" :value="department.id">
                        {{ department.name }}
                      </option>
                    </CSelect>
                  </CFormGroup>
                </CCol>
              </CRow>

              <CRow>
                <CCol>
                  <CFormGroup class="mb-0">
                    <CLabel>Comments</CLabel>
                    <CInput
                      :value="form.comments"
                      type="text"
                      placeholder="Enter Comments (optional)"
                      @input="form.comments = $event.target ? $event.target.value : $event"
                    />
                  </CFormGroup>
                </CCol>
              </CRow>
            </CCardBody>
            <CCardFooter>
              <CButton color="success" :disabled="isSaving" @click="savePriorityNumber">
                <CSpinner v-if="isSaving" size="sm" class="mr-1" />
                <span>Save</span>
              </CButton>
            </CCardFooter>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        <CCol xs="12">
          <CCard>
            <CCardHeader>
              <CCardTitle class="mb-0">Priority Call List</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <CAlert v-if="listError" color="danger" show>{{ listError }}</CAlert>

              <CRow>
                <CCol>
                  <CDataTable
                    :items="priorityList"
                    :fields="fields"
                    :no-items-view="{ noItems: 'There are no records to display' }"
                    :loading="isLoadingList"
                  >
                    <template #comments="{ item }">
                      <td>{{ item.comments || '-' }}</td>
                    </template>
                  </CDataTable>
                </CCol>
              </CRow>

              <CRow>
                <CCol xs="12" md="4">
                  <CPagination
                    :active-page="currentPage"
                    :pages="totalPages"
                    @update:activePage="onPageChange"
                  />
                </CCol>
                <CCol>
                  <CInput
                    type="date"
                    :value="filters.startDate"
                    @input="filters.startDate = $event.target ? $event.target.value : $event"
                  />
                </CCol>
                <CCol>
                  <CInput
                    type="date"
                    :value="filters.endDate"
                    @input="filters.endDate = $event.target ? $event.target.value : $event"
                  />
                </CCol>
                <CCol>
                  <CButton color="primary" :disabled="isExporting" @click="exportPriorityList">
                    <CSpinner v-if="isExporting" size="sm" class="mr-1" />
                    <span>Export</span>
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </template>
  </div>
</template>

<script>
import axios from 'axios'

function isoToday () {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

export default {
  name: 'AddPriorityNumber',
  data () {
    return {
      fields: ['contact_name', 'contact_phone', 'status', 'caller', 'comments', 'added_date'],
      departments: [],
      isLoadingDepartments: false,
      form: {
        customerPhone: '',
        customerName: '',
        departmentId: 0,
        comments: ''
      },
      formMessage: '',
      formMessageColor: '',
      alertDuration: 0,
      isSaving: false,
      priorityList: [],
      isLoadingList: false,
      listError: '',
      currentPage: 1,
      totalPages: 1,
      filters: {
        startDate: '2022-03-01',
        endDate: isoToday()
      },
      isExporting: false
    }
  },
  computed: {
    isModerator () {
      const roles = String(localStorage.getItem('roles') || '')
      return roles.toLowerCase().split(',').map(v => v.trim()).includes('moderator')
    },
    token () {
      return localStorage.getItem('api_token') || ''
    }
  },
  created () {
    this.loadDepartments()
    this.fetchPriorityList(1)
  },
  methods: {
    parseError (error, fallback) {
      const validationErrors = error?.response?.data?.error
      if (validationErrors && typeof validationErrors === 'object') {
        const key = Object.keys(validationErrors)[0]
        if (key && Array.isArray(validationErrors[key]) && validationErrors[key][0]) {
          return validationErrors[key][0]
        }
      }
      return error?.response?.data?.message || fallback
    },
    withTokenUrl (path) {
      const token = this.token
      if (!token) {
        return `${this.$apiAdress}${path}`
      }

      const joiner = path.includes('?') ? '&' : '?'
      return `${this.$apiAdress}${path}${joiner}token=${encodeURIComponent(token)}`
    },
    showFormMessage (message, color) {
      this.formMessage = message
      this.formMessageColor = color
      this.alertDuration = 10
    },
    async loadDepartments () {
      this.isLoadingDepartments = true

      try {
        const response = await axios.get(this.withTokenUrl('/api/department'))
        this.departments = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoadingDepartments = false
      }
    },
    async fetchPriorityList (page = 1) {
      this.isLoadingList = true
      this.listError = ''

      try {
        const response = await axios.get(this.withTokenUrl(`/api/calls_queue/priority_list?page=${page}`))
        const payload = response.data || {}
        this.currentPage = Number(payload.current_page || page || 1)
        this.priorityList = Array.isArray(payload.data) ? payload.data : []
        this.totalPages = Number(payload.last_page || 1)
      } catch (error) {
        console.log(error)
        this.priorityList = []
        this.listError = this.parseError(error, 'Unable to load priority call list.')
      } finally {
        this.isLoadingList = false
      }
    },
    async savePriorityNumber () {
      const phone = String(this.form.customerPhone || '')
      if (phone === '' || phone.length !== 10) {
        this.showFormMessage('Enter a 10 digit Phone Number', 'warning')
        return
      }

      this.isSaving = true

      try {
        const response = await axios.post(this.withTokenUrl('/api/call_queue'), {
          customer_phone: phone,
          customer_name: this.form.customerName,
          customer_department: this.form.departmentId,
          customer_comment: this.form.comments
        })

        if (response.status === 201) {
          const payload = response.data || {}
          this.showFormMessage(payload.message || 'Number Added to Calls Queue', payload.color || 'success')
          this.form.customerPhone = ''
          this.form.customerName = ''
          this.form.departmentId = 0
          this.form.comments = ''
          await this.fetchPriorityList(1)
        }
      } catch (error) {
        console.log(error)
        this.showFormMessage(this.parseError(error, 'Unable to add priority number.'), 'warning')
      } finally {
        this.isSaving = false
      }
    },
    onPageChange (page) {
      this.fetchPriorityList(Number(page || 1))
    },
    async exportPriorityList () {
      if (!this.filters.startDate || !this.filters.endDate) {
        this.listError = 'From Date and To Date are required.'
        return
      }

      this.listError = ''
      this.isExporting = true

      try {
        const response = await axios({
          url: this.withTokenUrl('/api/calls_queue/export_priority_list'),
          method: 'POST',
          data: {
            start_date: this.filters.startDate,
            end_date: this.filters.endDate
          },
          responseType: 'blob'
        })

        const blob = new Blob([response.data])
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'priority_list.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.log(error)
        this.listError = this.parseError(error, 'Unable to export priority call list.')
      } finally {
        this.isExporting = false
      }
    }
  }
}
</script>

<style scoped>
.page-shell {
  min-height: 100%;
}
</style>
